import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchProducts, fetchShopInfo } from "../lib/shopify";
import { usePirate, formatBerry } from "../lib/PirateContext";
import { useReviewsSummary } from "../lib/useReviewsSummary";
import ProductCard from "../components/ProductCard";
import EmptyShopBanner from "../components/EmptyShopBanner";
import { openCrewHierarchy } from "../components/CrewHierarchySheet";
import { THEMED_CATEGORIES } from "../lib/constants";
import {
    JollyRoger,
    TreasureChest,
    LogPose,
    WantedPoster,
    CaptainWheel,
    GrandRoute,
} from "../components/icons/PirateIcons";
import { ArrowRight, Compass, Shield, Truck, Sparkles } from "lucide-react";

const HERO_BG =
    "https://images.pexels.com/photos/34478699/pexels-photo-34478699.jpeg";

const CATEGORY_ICONS = {
    capitano: CaptainWheel,
    "grande-rotta": GrandRoute,
    tesori: TreasureChest,
    wanted: WantedPoster,
};

const CATEGORY_COLORS = {
    capitano: "bg-ink text-parchment",
    "grande-rotta": "bg-bounty text-parchment",
    tesori: "bg-gold text-ink",
    wanted: "bg-white text-ink",
};

const JOURNEY_CHAPTERS = [
    {
        num: "01",
        title: "SCEGLI LA TUA CIURMA",
        text: "Esplora le squadre più leggendarie del Grand Line. Dai capitani agli imperatori, ogni figura racconta una saga.",
        icon: CaptainWheel,
    },
    {
        num: "02",
        title: "CONQUISTA IL TESORO",
        text: "Aggiungi al bottino le tue figure preferite. Guadagna XP e Berry ad ogni azione da pirata.",
        icon: TreasureChest,
    },
    {
        num: "03",
        title: "SALPA VERSO CASA",
        text: "Checkout Shopify sicuro, imballo da collezionista, consegna in 48h in tutta Italia.",
        icon: LogPose,
    },
];

export default function Home() {
    const [all, setAll] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shop, setShop] = useState(null);
    const { level, berry } = usePirate();

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchProducts({ first: 30 }),
            fetchShopInfo().catch(() => null),
        ])
            .then(([products, shopInfo]) => {
                setAll(products);
                setFeatured(products.filter((p) => p.featured));
                setShop(shopInfo);
            })
            .catch((e) => console.error("Shopify fetch failed", e))
            .finally(() => setLoading(false));
    }, []);

    const isEmpty = !loading && all.length === 0;
    const showFeatured = featured.length ? featured : all.slice(0, 4);
    const allHandles = all.map((p) => p.handle).filter(Boolean);
    const reviewsMap = useReviewsSummary(allHandles);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative border-b-2 border-ink overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 bg-parchment/85" />
                <div className="absolute inset-0 halftone opacity-40" />

                <div className="absolute -right-40 -top-20 opacity-10 hidden lg:block">
                    <div className="slow-spin">
                        <LogPose size={600} className="text-ink" />
                    </div>
                </div>

                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-28 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 px-3 py-1 bg-ink text-parchment font-mono text-xs tracking-widest border-2 border-ink shadow-manga-red"
                        >
                            <span>★</span> ATTO 01 · IL LOG POSE PUNTA QUI
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="font-display text-7xl sm:text-8xl lg:text-[9rem] leading-[0.85] tracking-tighter mt-6"
                        >
                            IL VIAGGIO
                            <br />
                            <span className="text-bounty">DELLA</span>{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10">CIURMA</span>
                                <span className="absolute inset-x-0 bottom-2 h-4 bg-gold z-0" />
                            </span>
                            <br />
                            <span className="font-hand text-4xl sm:text-5xl lg:text-6xl text-ink/80 -rotate-2 inline-block mt-4">
                                inizia con una figure
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.25 }}
                            className="mt-8 text-base lg:text-lg max-w-xl text-ink/80 leading-relaxed"
                        >
                            Action figures premium via{" "}
                            <span className="font-bold">
                                {shop?.name || "Desa Shop"}
                            </span>
                            . Sistema di livelli da pirata, ricompense in Berry,
                            checkout Shopify sicuro. Non comprare —{" "}
                            <span className="font-bold">salpa</span>.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="mt-9 flex flex-wrap gap-4"
                        >
                            <Link
                                to="/figure"
                                className="btn-manga btn-manga-red text-lg"
                                data-testid="hero-shop-cta"
                            >
                                Esplora la Grande Rotta
                                <ArrowRight size={20} className="ml-2" />
                            </Link>
                            <Link
                                to="/figure?category=tesori"
                                className="btn-manga btn-manga-gold"
                                data-testid="hero-tesori-cta"
                            >
                                <TreasureChest size={18} className="mr-2" />
                                I Tesori
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.55 }}
                            className="mt-10 flex items-center gap-4 max-w-lg"
                        >
                            <button
                                type="button"
                                onClick={openCrewHierarchy}
                                className="bounty-card px-4 py-3 flex-1 flex items-center gap-3 hover:shadow-manga-lg transition-manga text-left cursor-pointer group"
                                data-testid="hero-open-hierarchy"
                                aria-label="Apri gerarchia della ciurma"
                            >
                                <div className="w-10 h-10 bg-bounty text-parchment border-2 border-ink flex items-center justify-center font-display text-xl">
                                    {level.level}
                                </div>
                                <div className="flex-1">
                                    <div className="font-hand text-xs text-ink/60 -mb-1">
                                        stai navigando come
                                    </div>
                                    <div className="font-display tracking-wide">
                                        {level.name.toUpperCase()}
                                    </div>
                                </div>
                                <ArrowRight
                                    size={16}
                                    className="opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-manga"
                                />
                            </button>
                            <div className="bounty-card px-4 py-3 bg-gold flex items-center gap-2">
                                <Sparkles size={16} />
                                <div>
                                    <div className="font-hand text-xs -mb-1">
                                        tesoro
                                    </div>
                                    <div
                                        className="font-mono text-sm font-bold"
                                        data-testid="hero-berry"
                                    >
                                        {formatBerry(berry)}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Wanted poster */}
                    <div className="lg:col-span-4 hidden lg:flex justify-end">
                        <motion.div
                            initial={{ opacity: 0, rotate: -6, y: 40 }}
                            animate={{ opacity: 1, rotate: 3, y: 0 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.3,
                                type: "spring",
                            }}
                            className="relative w-full max-w-xs"
                        >
                            <div className="wanted-paper border-2 border-ink shadow-manga-lg p-6 float-y">
                                <div className="font-hand text-2xl text-bounty text-center">
                                    WANTED
                                </div>
                                <div className="font-display text-4xl tracking-tight text-center">
                                    DEAD OR ALIVE
                                </div>
                                <div className="mt-3 aspect-square halftone-red border-2 border-ink flex items-center justify-center">
                                    <JollyRoger
                                        size={100}
                                        className="text-ink"
                                    />
                                </div>
                                <div className="mt-3 font-mono text-xs text-ink/60 text-center">
                                    TAGLIA STIMATA
                                </div>
                                <div className="font-display text-3xl text-bounty text-center">
                                    ∞ BERRY
                                </div>
                            </div>
                            <div className="absolute -bottom-4 -left-4 bg-bounty text-parchment text-xs font-mono border-2 border-ink shadow-manga p-2 -rotate-6">
                                NUOVO ARRIVO
                            </div>
                            <div className="tape -top-3 left-1/2 -translate-x-1/2" />
                        </motion.div>
                    </div>
                </div>
            </section>

            <div className="wave-divider" />

            {/* Themed categories */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex items-end justify-between mb-10 flex-wrap gap-4"
                >
                    <div>
                        <div className="font-hand text-2xl text-bounty dashed-underline inline-block">
                            scegli il tuo destino
                        </div>
                        <h2 className="font-display text-5xl lg:text-6xl tracking-tight mt-3">
                            LE 4 <span className="text-outline">STROFE</span>{" "}
                            DEL VIAGGIO
                        </h2>
                    </div>
                    <Link
                        to="/figure"
                        className="hidden md:inline-flex font-display tracking-widest border-b-2 border-ink hover:text-bounty hover:border-bounty"
                    >
                        VEDI TUTTI →
                    </Link>
                </motion.div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="themed-categories-grid">
                    {THEMED_CATEGORIES.map((c, i) => {
                        const Icon = CATEGORY_ICONS[c.slug];
                        const colorClass = CATEGORY_COLORS[c.slug];
                        return (
                            <motion.div
                                key={c.slug}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                            >
                                <Link
                                    to={`/figure${c.param}`}
                                    className={`tilt-card block border-2 border-ink shadow-manga min-h-[240px] p-6 relative overflow-hidden group ${colorClass}`}
                                    data-testid={`themed-tile-${c.slug}`}
                                >
                                    <div className="absolute inset-0 halftone opacity-10 group-hover:opacity-25 transition-manga" />
                                    <div className="relative">
                                        <Icon
                                            size={56}
                                            className="mb-3 opacity-90"
                                        />
                                        <div className="font-hand text-sm opacity-70 mb-1">
                                            {c.subtitle}
                                        </div>
                                        <div className="font-display text-4xl tracking-tighter leading-none">
                                            {c.label}
                                        </div>
                                        <div className="absolute bottom-0 right-0 font-hand text-3xl opacity-0 group-hover:opacity-100 transition-manga">
                                            →
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* Journey chapters */}
            <section className="relative bg-ink text-parchment border-y-2 border-ink py-24 overflow-hidden">
                <div className="absolute inset-0 halftone opacity-5" />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
                    <div className="text-center mb-14">
                        <div className="font-hand text-2xl text-gold">
                            - il tuo diario di viaggio -
                        </div>
                        <h2 className="font-display text-5xl lg:text-7xl tracking-tight mt-2">
                            TRE CAPITOLI,<br />
                            <span className="text-bounty">UN&apos;AVVENTURA</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {JOURNEY_CHAPTERS.map((ch, i) => {
                            const Icon = ch.icon;
                            return (
                                <motion.div
                                    key={ch.num}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.15 }}
                                    className="relative"
                                >
                                    <div className="absolute -top-4 -left-2 font-display text-8xl text-bounty/40 leading-none">
                                        {ch.num}
                                    </div>
                                    <div className="relative border-2 border-parchment p-6 bg-ink shadow-manga-red">
                                        <Icon
                                            size={48}
                                            className="text-gold mb-4"
                                        />
                                        <div className="font-display text-3xl tracking-tight">
                                            {ch.title}
                                        </div>
                                        <p className="mt-3 text-sm text-parchment/70 leading-relaxed">
                                            {ch.text}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured / Empty state */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div>
                        <div className="font-hand text-2xl text-bounty dashed-underline inline-block">
                            wanted collection
                        </div>
                        <h2 className="font-display text-5xl lg:text-6xl tracking-tight mt-3">
                            PIRATI PIÙ RICERCATI
                        </h2>
                    </div>
                </div>

                {isEmpty ? (
                    <EmptyShopBanner />
                ) : (
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
                        data-testid="featured-grid"
                    >
                        {loading
                            ? Array.from({ length: 4 }).map((_, i) => (
                                  <div
                                      key={i}
                                      className="bounty-card aspect-[4/5] bg-seafoam animate-pulse"
                                  />
                              ))
                            : showFeatured.map((p, i) => (
                                  <motion.div
                                      key={p.id}
                                      initial={{ opacity: 0, y: 30 }}
                                      whileInView={{ opacity: 1, y: 0 }}
                                      viewport={{ once: true }}
                                      transition={{ delay: i * 0.08 }}
                                  >
                                      <ProductCard
                                          product={p}
                                          reviewSummary={reviewsMap[p.handle]}
                                      />
                                  </motion.div>
                              ))}
                    </div>
                )}
            </section>

            {/* Perks strip */}
            <section className="border-y-2 border-ink py-14 bg-seafoam relative overflow-hidden">
                <div className="absolute inset-0 halftone-gold opacity-30" />
                <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[
                        { Icon: Truck, k: "SPEDIZIONE 48H", v: "in tutta Italia" },
                        { Icon: Shield, k: "CHECKOUT SHOPIFY", v: "sicuro & rapido" },
                        { Icon: Compass, k: "SISTEMA XP", v: "guadagna Berry" },
                        { Icon: TreasureChest, k: "IMBALLO PREMIUM", v: "da collezionista" },
                    ].map((p, i) => (
                        <motion.div
                            key={p.k}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center gap-4"
                        >
                            <p.Icon size={36} className="text-ink flex-shrink-0" />
                            <div>
                                <div className="font-display tracking-tight text-lg">
                                    {p.k}
                                </div>
                                <div className="font-hand text-sm text-ink/70">
                                    {p.v}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* All products */}
            {!isEmpty && (
                <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
                    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                        <div>
                            <div className="font-hand text-2xl text-bounty dashed-underline inline-block">
                                la flotta completa
                            </div>
                            <h2 className="font-display text-5xl lg:text-6xl tracking-tight mt-3">
                                TUTTO IL BOTTINO
                            </h2>
                        </div>
                        <Link
                            to="/figure"
                            className="btn-manga"
                            data-testid="home-view-all-btn"
                        >
                            Vedi tutto
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {all.slice(0, 8).map((p, i) => (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: (i % 4) * 0.06 }}
                            >
                                <ProductCard
                                    product={p}
                                    reviewSummary={reviewsMap[p.handle]}
                                />
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {/* CTA - Join the crew */}
            <section className="relative py-24 overflow-hidden bg-bounty text-parchment border-y-2 border-ink">
                <div className="absolute inset-0 halftone-red opacity-40" />
                <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center">
                    <JollyRoger
                        size={80}
                        className="mx-auto mb-6 text-parchment"
                    />
                    <div className="font-hand text-3xl">
                        - stanco di navigare da solo? -
                    </div>
                    <h3 className="font-display text-5xl md:text-7xl tracking-tight mt-2">
                        UNISCITI ALLA CIURMA
                    </h3>
                    <p className="mt-4 text-parchment/80 max-w-xl mx-auto">
                        Iscriviti e ricevi{" "}
                        <span className="font-bold">10% di sconto</span>,{" "}
                        <span className="font-bold">100 XP</span> e{" "}
                        <span className="font-bold">500 Berry</span> per iniziare
                        il tuo viaggio.
                    </p>
                    <button
                        onClick={() => {
                            localStorage.removeItem(
                                "desashop_newsletter_shown"
                            );
                            window.location.reload();
                        }}
                        className="btn-manga btn-manga-gold mt-8 text-lg"
                        data-testid="cta-join-crew-btn"
                    >
                        Alza le vele
                    </button>
                </div>
            </section>
        </div>
    );
}
